import { useEffect, useId, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { SHEET_APPROVAL_TYPE } from "@/payroll/payroll.d";
import { getApprovalGate } from "./approval-types";
import type { ApprovalActionTarget } from "./types";
import { summarizeSheetNames } from "./utils";

export const APPROVAL_REASON_MAX_LENGTH = 2000;
// Counting up from zero on an optional field is noise; the count earns its place
// once the limit is actually in reach.
const COUNTER_VISIBLE_FROM = APPROVAL_REASON_MAX_LENGTH - 200;

interface ApprovalRemovalDialogProps {
  target: ApprovalActionTarget | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason?: string) => void;
}

/**
 * Taking an approval back. Not styled as a destructive action: nothing is
 * deleted — the history keeps every action, and this appends the one that says
 * the sign-off no longer stands. The reason is optional and goes into that
 * record, which is the whole point of asking for it here.
 */
export const ApprovalRemovalDialog = ({
  target,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: ApprovalRemovalDialogProps) => {
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const open = Boolean(target);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const gate = getApprovalGate(
    target?.approvalType ?? SHEET_APPROVAL_TYPE.readyForTechApproval,
  );
  const accessKey = target?.accessKey;
  const trimmedReason = reason.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSubmitting) onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove “{gate.label}”?</DialogTitle>
          <DialogDescription className="sr-only">
            Remove the {gate.label} approval
          </DialogDescription>
        </DialogHeader>
        <Stack gap={4} className="w-full">
          <Stack
            align="start"
            gap={0}
            className="w-full rounded-lg border border-line bg-neutral px-4 py-3"
          >
            <Text variant="label">
              {target?.sheet?.name ?? accessKey?.name ?? "This sheet"}
            </Text>
            {accessKey && (
              <Text variant="caption" tone="muted">
                {target?.sheet
                  ? accessKey.name
                  : summarizeSheetNames(accessKey.sheet_names)}
              </Text>
            )}
          </Stack>
          <Text tone="muted">
            {target?.sheet
              ? "This sheet goes back to not approved."
              : "These sheets go back to not approved."}{" "}
            You can approve again at any time. Every change is saved.
          </Text>
          <Stack className="w-full" gap={2}>
            <Label htmlFor={reasonId}>Reason (optional)</Label>
            <Textarea
              id={reasonId}
              value={reason}
              maxLength={APPROVAL_REASON_MAX_LENGTH}
              disabled={isSubmitting}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Example: overtime hours for two techs still need checking"
            />
            {reason.length >= COUNTER_VISIBLE_FROM && (
              <Text variant="caption" tone="muted">
                {reason.length.toLocaleString()} of{" "}
                {APPROVAL_REASON_MAX_LENGTH.toLocaleString()} characters
              </Text>
            )}
          </Stack>
        </Stack>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Keep approval
          </Button>
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={() => onConfirm(trimmedReason || undefined)}
          >
            Remove approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
